/*=============================================== ComponentsLayout component ===============================================*/

import { useLocation } from "react-router-dom"
import {
    Text,
    CodeContainer,
    slugify,
    unslugify,
    convertToPascal,
} from "tsx-library-julseb"

import { Page, DemoItem, PropsItems } from "components"

import { StyledCode, Section } from "./styles"
import type {
    ComponentsLayoutProps,
    PropsItemType,
} from "components/layouts/ComponentsLayout/types"
import { allPages } from "data"

const ComponentsLayout = ({
    children,
    items,
    propsItems,
}: ComponentsLayoutProps) => {
    const location = useLocation().pathname
    const componentName = location.split("/")[2]

    const pageComponent = allPages.find(
        component => slugify(component.title) === slugify(componentName)
    )

    if (!pageComponent) throw new Error("Error while fetching page")

    const { title, intro, include, types, as, extend } = pageComponent
    const titleClean = unslugify(title)

    const importStr = `import { ${
        typeof include === "object"
            ? include.join(", ")
            : convertToPascal(titleClean)
    } } from "tsx-library-julseb"`

    const importsTitle = `File${
        (types && include) ||
        (include && typeof include !== "boolean" && include?.length > 1) ||
        (include !== false && types)
            ? "s"
            : ""
    } to import`

    const showAs = as
        ? typeof as === "string"
            ? as?.toString()
            : as
        : undefined

    const asItem: PropsItemType = {
        subtitle: "as",
        intro: "Changes the HTML tag",
        type: "string",
        possibleString: "Any HTML tag",
        // @ts-expect-error
        default: showAs,
    }

    return (
        <Page title={titleClean}>
            <Text tag="h1">{titleClean}</Text>

            {intro && (
                <Section>
                    {typeof intro === "string" ? <Text>{intro}</Text> : intro}
                </Section>
            )}

            {(include !== false || types) && (
                <>
                    <Text tag="h3">{importsTitle}</Text>

                    <StyledCode>
                        <CodeContainer language="javascript">
                            {types && include !== false
                                ? `${importStr}\nimport type { ${types.join(
                                      ", "
                                  )} } from "tsx-library-julseb/types"`
                                : include === false && types
                                ? `import type { ${types.join(
                                      ", "
                                  )} } from "tsx-library-julseb/types"`
                                : importStr}
                        </CodeContainer>
                    </StyledCode>
                </>
            )}

            {children}

            {items && items.map((item, i) => <DemoItem item={item} key={i} />)}

            {propsItems && (
                <PropsItems
                    items={as ? [asItem, ...propsItems] : propsItems}
                    isExtending={extend}
                />
            )}
        </Page>
    )
}

export default ComponentsLayout
